import React, {useCallback, useRef, useState, useEffect} from "react"
import clsx from "clsx"
import moment from "moment"
import {startCase} from "lodash"
import * as XLSX from "xlsx"
import ModalV2 from "../../../components/portal/ModalV2"
import {platformAPI} from "../../../api/axiosConfig"
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector"

// ---- Types -----------------------------------------------------------------

export type SirenAlert = {
    id: string
    name: string
    content: string
    smsContent: string
    useSeparateSMSContent: boolean
    smsSenderID?: string
    status: string
    sendAt: string
    createdBy?: string
    templateID?: string
    templateName?: string
    responseOptions: string[]
    deliveryMethods: string[]
    broadcastConfig: BroadcastConfig | null
    useDefaultBroadcastConfig: boolean
    useDefaultDeliveryMethods: boolean
}

export type BroadcastConfig = {
    cycleCount: number
    cycleInterval: number
    cycleIntervalDisplayFormat: string
    deliveryMethodInterval: number
    deliveryMethodIntervalDisplayFormat: string
    broadcastDuration: number
    broadcastDurationDisplayFormat: string
}

// ---- Colors ----------------------------------------------------------------

export const STATUS_COLORS: Record<string, string> = {
    in_progress: "#2699FB",
    completed:   "#1E9B26",
    expired:     "#E6920A",
    cancelled:   "#E20000",
    pending:     "#B2B1B1",
}

export const RESPONSE_COLORS = [
    "#8c8a8a",
    "#4e79a7",
    "#f28e2b",
    "#59a14f",
    "#76b7b2",
    "#af7aa1",
    "#ffbb28",
    "#ff9da7",
    "#9c755f",
    "#17becf",
    "#e15759",
]

// ---- Formatters ------------------------------------------------------------

export function statusLabel(status: string): string {
    switch (status) {
        case "in_progress": return "In Progress"
        case "completed":   return "Completed"
        case "expired":     return "Expired"
        case "cancelled":   return "Cancelled"
        case "pending":     return "Pending"
    }
    return startCase(status)
}

export function deliveryMethodLabel(method: string): string {
    switch (method) {
        case "sms_work_mobile":       return "SMS Work Mobile"
        case "sms_personal_mobile":   return "SMS Personal Mobile"
        case "email_work":            return "Work Email"
        case "email_personal":        return "Personal Email"
        case "call_work_mobile":      return "Call Work Mobile"
        case "call_personal_mobile":  return "Call Personal Mobile"
        case "call_home":             return "Call Home"
    }
    return startCase(method)
}

export function formatSkipReason(reason: string, deliveryMethod: string): string {
    if (!reason) return ""
    switch (reason) {
        case "no_address": {
            if (deliveryMethod.startsWith("sms_") || deliveryMethod.startsWith("call_")) return "No phone number"
            if (deliveryMethod === "email_work" || deliveryMethod === "email_personal") return "No email address"
            return "No address"
        }
        case "no_opt_in":        return "Not opted in"
        case "no_driver":        return "Sending not configured"
        case "driver_not_found": return "Sending not configured"
        case "send_failed":      return "Failed to send"
        case "callback_timeout": return "No delivery confirmation received"
        case "failed":           return "Failed"
        case "undelivered":      return "Undelivered"
    }
    if (reason.startsWith("driver_build_error:")) return "Sending not configured"
    return startCase(reason)
}

export function formatSeconds(value: number): string {
    if (value >= 3600) return `${Math.round(value / 3600)}h`
    if (value >= 60) return `${Math.round(value / 60)}m`
    return `${value}s`
}

export function formatResponseVia(via: string | null): string {
    if (!via) return "—"
    switch (via) {
        case "sms_reply":   return "SMS Reply"
        case "portal_link": return "Response Link"
    }
    return startCase(via)
}

// ---- Shared components -----------------------------------------------------

export function Card({title, headerActions, children, className}: {
    title?: string
    headerActions?: React.ReactNode
    children: React.ReactNode
    className?: string
}) {
    return (
        <div className={clsx("bg-white rounded-lg shadow-md p-5 flex flex-col gap-4", className)}>
            {(title || headerActions) && (
                <div className="flex items-center justify-between">
                    {title && <h2 className="text-base font-semibold text-dark-dark-grey">{title}</h2>}
                    {headerActions}
                </div>
            )}
            {children}
        </div>
    )
}

export function PieTooltip({active, payload}: {active?: boolean; payload?: {name: string; value: number; payload: {color: string}}[]}) {
    if (!active || !payload?.length) return null
    const {name, value, payload: {color}} = payload[0]
    return (
        <div className="bg-white border border-light-grey rounded-lg shadow-md px-3 py-2 flex items-center gap-2 text-xs pointer-events-none">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{background: color}} />
            <span className="font-medium text-dark-dark-grey">{name}</span>
            <span className="text-grey">{value}</span>
        </div>
    )
}

export function InfoRow({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <>
            <dt className="text-sm font-medium text-grey">{label}</dt>
            <dd className="text-sm text-dark-dark-grey">{value}</dd>
        </>
    )
}

// ---- Cycle tab selector ----------------------------------------------------

export function CycleTabSelector({cycles, selected, onChange}: {
    cycles: number[]
    selected: number | "all"
    onChange: (c: number | "all") => void
}) {
    if (cycles.length <= 1) return null
    return (
        <div className="flex rounded overflow-hidden border border-light-grey text-xs font-medium">
            <button
                onClick={() => onChange("all")}
                className={clsx("px-3 py-1.5 transition-colors", selected === "all" ? "bg-blue text-white" : "bg-white text-grey hover:bg-ice")}
            >
                All
            </button>
            {cycles.map(c => (
                <button
                    key={c}
                    onClick={() => onChange(c)}
                    className={clsx("px-3 py-1.5 transition-colors", selected === c ? "bg-blue text-white" : "bg-white text-grey hover:bg-ice")}
                >
                    Cycle {c}
                </button>
            ))}
        </div>
    )
}

// ---- Response recipients modal ---------------------------------------------

type RecipientRow = {
    id: string
    firstName: string
    lastName: string
    email: string
    personalEmail: string
    workMobileE164: string
    personalMobileE164: string
    homePhoneE164: string
    currentCycle: number
    responseCycle: number | null
    responseMethod: string | null
    responseVia: string | null
    responseValue: string | null
    respondedAt: string | null
}

export function ResponseRecipientsModal({alertID, label, rawValue, onClose}: {
    alertID: string
    label: string
    rawValue: string
    onClose: () => void
}) {
    const headers = rawValue === "__invalid__"
        ? ["Name", "Response", "Responded At", "Cycle", "Response Method"]
        : ["Name", "Responded At", "Cycle", "Response Method"]

    const getItems = useCallback(async (search: string, page: number) => {
        const params: Record<string, string | number> = {page, limit: 10, search}
        if (rawValue !== "__invalid__") params.responseValue = rawValue
        const res = await platformAPI.get<{results: RecipientRow[]; hasMore: boolean; currentPage: number}>(`api/siren/alerts/${alertID}/recipients`, {params})
        return {results: res.data.results, hasMore: res.data.hasMore, currentPage: res.data.currentPage}
    }, [alertID, rawValue])

    const getColumns = useCallback((r: RecipientRow) => {
        const cols: string[] = [`${r.firstName} ${r.lastName}`]
        if (rawValue === "__invalid__") cols.push(r.responseValue ?? "—")
        cols.push(
            r.respondedAt ? moment(r.respondedAt).local().format("DD/MM/YYYY HH:mm:ss") : "—",
            String(r.responseCycle ?? r.currentCycle),
            formatResponseVia(r.responseVia),
        )
        return cols
    }, [rawValue])

    const [downloading, setDownloading] = useState(false)
    const downloadGroup = useCallback(async () => {
        setDownloading(true)
        try {
            let page = 1
            let allRows: RecipientRow[] = []
            while (true) {
                const data = await getItems("", page)
                allRows = [...allRows, ...data.results]
                if (!data.hasMore) break
                page++
            }
            const sheetData = [
                ["Name", "Email", "Personal Email", "Work Mobile", "Personal Mobile", "Home Phone", "Response", "Responded At", "Cycle", "Response Method"],
                ...allRows.map(r => [
                    `${r.firstName} ${r.lastName}`,
                    r.email,
                    r.personalEmail,
                    r.workMobileE164,
                    r.personalMobileE164,
                    r.homePhoneE164,
                    r.responseValue ?? "",
                    r.respondedAt ? moment(r.respondedAt).format("DD/MM/YYYY HH:mm:ss") : "",
                    r.responseCycle ?? r.currentCycle,
                    formatResponseVia(r.responseVia),
                ]),
            ]
            const ws = XLSX.utils.aoa_to_sheet(sheetData)
            ws["!cols"] = [
                {wch: 24}, {wch: 28}, {wch: 28}, {wch: 18}, {wch: 18}, {wch: 16},
                {wch: 28}, {wch: 20}, {wch: 8},  {wch: 16},
            ]
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 31))
            XLSX.writeFile(wb, `${label.replace(/\s+/g, "_")}_${alertID}.xlsx`)
        } finally {
            setDownloading(false)
        }
    }, [alertID, label, rawValue, getItems])

    return (
        <ModalV2 open setOpen={onClose}>
            <div className="flex items-center justify-between pr-6">
                <h2 className="text-base font-semibold text-dark-dark-grey">{label}</h2>
                <button
                    onClick={downloadGroup}
                    disabled={downloading}
                    className="px-3 py-1.5 rounded bg-blue text-white text-xs font-medium disabled:opacity-60"
                >
                    {downloading ? "Downloading…" : "Download"}
                </button>
            </div>
            <PaginatedSelector
                headers={headers}
                getItems={getItems}
                getColumns={getColumns}
                disableSelect
            />
        </ModalV2>
    )
}

// ---- Delivery attempts modal -----------------------------------------------

type AttemptRow = {
    id: string
    recipientID: string
    firstName: string
    lastName: string
    cycle: number
    deliveryMethod: string
    from: string
    to: string
    status: string
    skipReason: string
    createdAt: string
    sentAt: string | null
    failedAt: string | null
}

export function DeliveryAttemptsModal({alertID, label, category, onClose}: {
    alertID: string
    label: string
    category: string
    onClose: () => void
}) {
    const headers = ["Name", "Method", "Status", "Skip Reason", "Cycle", "Sent At"]

    const getItems = useCallback(async (search: string, page: number) => {
        const res = await platformAPI.get<{results: AttemptRow[]; hasMore: boolean; currentPage: number}>(`api/siren/alerts/${alertID}/attempts`, {
            params: {category, page, limit: 10, search},
        })
        return {results: res.data.results, hasMore: res.data.hasMore, currentPage: res.data.currentPage}
    }, [alertID, category])

    const getColumns = useCallback((a: AttemptRow) => [
        `${a.firstName} ${a.lastName}`,
        deliveryMethodLabel(a.deliveryMethod),
        startCase(a.status),
        formatSkipReason(a.skipReason, a.deliveryMethod) || "—",
        String(a.cycle),
        a.sentAt ? moment(a.sentAt).local().format("DD/MM/YYYY HH:mm:ss") : "—",
    ], [])

    const [downloading, setDownloading] = useState(false)
    const download = useCallback(async () => {
        setDownloading(true)
        try {
            let page = 1
            let allRows: AttemptRow[] = []
            while (true) {
                const data = await getItems("", page)
                allRows = [...allRows, ...data.results]
                if (!data.hasMore) break
                page++
            }
            const sheetData = [
                ["ID", "Recipient ID", "First Name", "Last Name", "Cycle", "Delivery Method", "From", "To", "Status", "Skip Reason", "Sent At", "Failed At"],
                ...allRows.map(a => [
                    a.id,
                    a.recipientID,
                    a.firstName,
                    a.lastName,
                    a.cycle,
                    a.deliveryMethod,
                    a.from,
                    a.to,
                    startCase(a.status),
                    formatSkipReason(a.skipReason, a.deliveryMethod),
                    a.sentAt ? moment(a.sentAt).format("DD/MM/YYYY HH:mm:ss") : "",
                    a.failedAt ? moment(a.failedAt).format("DD/MM/YYYY HH:mm:ss") : "",
                ]),
            ]
            const ws = XLSX.utils.aoa_to_sheet(sheetData)
            ws["!cols"] = [
                {wch: 36}, {wch: 36}, {wch: 14}, {wch: 18}, {wch: 8},  {wch: 18},
                {wch: 20}, {wch: 20}, {wch: 14}, {wch: 28}, {wch: 20}, {wch: 20},
            ]
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 31))
            XLSX.writeFile(wb, `${label.replace(/\s+/g, "_")}_attempts_${alertID}.xlsx`)
        } finally {
            setDownloading(false)
        }
    }, [alertID, label, getItems])

    return (
        <ModalV2 open setOpen={onClose}>
            <div className="flex items-center justify-between pr-6">
                <h2 className="text-base font-semibold text-dark-dark-grey">{label} Attempts</h2>
                <button
                    onClick={download}
                    disabled={downloading}
                    className="px-3 py-1.5 rounded bg-blue text-white text-xs font-medium disabled:opacity-60"
                >
                    {downloading ? "Downloading…" : "Download"}
                </button>
            </div>
            <PaginatedSelector
                headers={headers}
                getItems={getItems}
                getColumns={getColumns}
                disableSelect
            />
        </ModalV2>
    )
}

// ---- Template search select ------------------------------------------------

export type MassWarningTemplate = {
    id: string
    name: string
}

export function TemplateSearchSelect({templates, value, onSearch, onChange}: {
    templates: MassWarningTemplate[]
    value: MassWarningTemplate | null
    onSearch: (s: string) => void
    onChange: (t: MassWarningTemplate | null) => void
}) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState("")
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handler)
        return () => document.removeEventListener("mousedown", handler)
    }, [open])

    const handleSearch = (s: string) => {
        setSearch(s)
        onSearch(s)
    }

    const select = (t: MassWarningTemplate | null) => {
        onChange(t)
        setSearch("")
        onSearch("")
        setOpen(false)
    }

    return (
        <div ref={containerRef} className="relative flex-1">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between border border-light-grey rounded px-3 py-1.5 text-xs bg-white hover:bg-ice transition-colors"
            >
                <span className={value ? "text-dark-dark-grey font-medium" : "text-grey"}>
                    {value ? value.name : "Select template (optional)"}
                </span>
                <span className="text-grey ml-2">▾</span>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-light-grey rounded shadow-md flex flex-col">
                    <input
                        autoFocus
                        type="text"
                        value={search}
                        onChange={e => handleSearch(e.target.value)}
                        placeholder="Search templates…"
                        className="px-3 py-2 text-xs border-b border-light-grey outline-none"
                    />
                    <div className="max-h-48 overflow-y-auto">
                        <button
                            type="button"
                            onClick={() => select(null)}
                            className="w-full text-left px-3 py-2 text-xs text-grey hover:bg-ice"
                        >
                            No template
                        </button>
                        {templates.map(t => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => select(t)}
                                className={clsx("w-full text-left px-3 py-2 text-xs hover:bg-ice", value?.id === t.id ? "text-blue font-medium" : "text-dark-dark-grey")}
                            >
                                {t.name}
                            </button>
                        ))}
                        {templates.length === 0 && (
                            <p className="px-3 py-2 text-xs text-grey">No templates found</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
